import { XMarkIcon } from "@heroicons/react/20/solid";
import { FieldArray, Form, Formik, FormikHelpers } from "formik";
import { useMemo } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { CheckboxWithLabel } from "@/core/components/checkbox-with-label";
import { Button } from "@/core/components/ui/button";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { UserSelector } from "@/core/components/user-selector";
import { useUpdateReviewUsers } from "@/activity-feed/query";
import { useBusiness } from "@/core/business";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { useGetActiveSpiffRules } from "@/spiff-rules/query";

interface ReviewAttributionContentProps {
  reviewId: string;
  onClose: () => void;
}

interface ReviewAttributionFormValues {
  userIds: string[];
  createSpiffs: boolean;
  approveSpiffs: boolean;
}

const INITIAL_VALUES: ReviewAttributionFormValues = {
  userIds: [""],
  createSpiffs: false,
  approveSpiffs: false,
};

const validationSchema = Yup.object().shape({
  userIds: Yup.array()
    .of(Yup.string())
    .min(1, "At least one user is required")
    .test("no-duplicates", "Duplicate users are not allowed", (value) => {
      if (!value) return true;
      const nonEmpty = value.filter((userId) => userId !== "");
      return nonEmpty.length === new Set(nonEmpty).size;
    })
    .test(
      "at-least-one-selected",
      "At least one user must be selected",
      (value) => {
        if (!value) return false;
        return value.some((userId) => userId !== "");
      },
    ),
  createSpiffs: Yup.boolean(),
  approveSpiffs: Yup.boolean(),
});

export const ReviewAttributionContent = ({
  reviewId,
  onClose,
}: ReviewAttributionContentProps) => {
  const { businessId } = useBusiness();
  const { mutateAsync: updateReviewUsers, isPending } =
    useUpdateReviewUsers(businessId);
  const { data: activeSpiffRules = [] } = useGetActiveSpiffRules(businessId);

  const hasActiveReviewSpiffRule = useMemo(
    () =>
      activeSpiffRules.some(
        (rule) => rule.event === SPIFF_RULE_EVENTS.five_star_review,
      ),
    [activeSpiffRules],
  );

  const handleSubmit = async (
    values: ReviewAttributionFormValues,
    { setSubmitting }: FormikHelpers<ReviewAttributionFormValues>,
  ) => {
    try {
      const filteredUserIds = values.userIds.filter((userId) => userId !== "");

      if (filteredUserIds.length === 0) {
        toast.error("Please select at least one user");
        setSubmitting(false);
        return;
      }

      await updateReviewUsers({
        reviewId,
        userIds: filteredUserIds,
        createSpiffs: values.createSpiffs,
        approveSpiffs: values.approveSpiffs,
      });

      toast.success("Review attribution updated");
      onClose();
    } catch {
      toast.error("Failed to update review attribution");
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add users to review</DialogTitle>
      </DialogHeader>

      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        validateOnBlur
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <Stack gap="lg" className="p-6 pt-0" fullWidth>
              <Stack gap="md" fullWidth>
                <FieldArray name="userIds">
                  {({ push, remove }) => (
                    <Stack gap="sm" fullWidth>
                      {values.userIds.map((userId, index) => (
                        <Stack
                          key={index}
                          direction="row"
                          align="center"
                          gap="sm"
                          fullWidth
                        >
                          <UserSelector
                            businessId={businessId}
                            value={userId}
                            onChange={(value) =>
                              setFieldValue(`userIds.${index}`, value)
                            }
                            placeholder="Select a user"
                            fullWidth
                          />
                          {values.userIds.length > 1 && (
                            <Button
                              type="button"
                              variant="icon"
                              size="iconSm"
                              onClick={() => remove(index)}
                              className="mb-1 shrink-0"
                            >
                              <XMarkIcon className="size-5 text-tertiary-text" />
                            </Button>
                          )}
                        </Stack>
                      ))}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => push("")}
                      >
                        Add User
                      </Button>
                    </Stack>
                  )}
                </FieldArray>
                {errors.userIds && touched.userIds && (
                  <Typography variant="captionW1" className="text-red-500">
                    {typeof errors.userIds === "string"
                      ? errors.userIds
                      : "Please check user selections"}
                  </Typography>
                )}
              </Stack>

              {hasActiveReviewSpiffRule && (
                <Stack gap="xs">
                  <CheckboxWithLabel
                    id="createSpiffs"
                    checked={values.createSpiffs}
                    onCheckedChange={(checked) => {
                      setFieldValue("createSpiffs", checked);
                      if (!checked) {
                        setFieldValue("approveSpiffs", false);
                      }
                    }}
                    label="Create spiffs for these users"
                  />
                  {values.createSpiffs && (
                    <CheckboxWithLabel
                      id="approveSpiffs"
                      checked={values.approveSpiffs}
                      onCheckedChange={(checked) =>
                        setFieldValue("approveSpiffs", checked)
                      }
                      label="Automatically approve spiffs"
                      className="ml-6"
                    />
                  )}
                </Stack>
              )}
            </Stack>

            <DialogFooter>
              <Button type="button" variant="tertiary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </>
  );
};
